/**
 * Per-viewer roof-hiding: the pure, client-only render decision UO made too.
 * Given the local player's tile and the map's statics, it returns the exact set
 * of roof statics to cull (hide) for *this* viewer this frame.
 *
 * It imports no Pixi and reads no wall clock, so it is fully headless-testable.
 * It is a *render-time* decision per client: the authoritative world the server
 * broadcasts is identical for everyone, and only what each browser draws differs
 * - a player inside the bank sees no roof while a player outside still sees it.
 *
 * Roof statics are identified solely by the `roof` flag baked into the map from
 * the real UO `tiledata` ROOF flag during extraction (never guessed here). They
 * are grouped into connected *structures* by 8-adjacency of their tile
 * footprints, so two buildings that happen to share the crop resolve
 * independently. When the local player's tile falls inside a structure's roof
 * footprint, that whole structure's roof is returned hidden; every other
 * structure stays visible.
 */

/** The minimum the decision needs off a static: its tile and whether it is roof. */
export interface RoofCandidate {
  x: number;
  y: number;
  roof?: boolean;
}

interface Tile {
  x: number;
  y: number;
}

export const key = (x: number, y: number): string => `${x},${y}`;

/**
 * One connected roof *structure*: its member roof statics and the set of tile
 * keys (`"x,y"`) its cap covers. The footprint doubles as the building's
 * inside/outside *region* - a tile inside the footprint is "under" (inside) this
 * building, a tile in no structure's footprint is the open plaza. Both
 * per-viewer roof-hiding (this module, ADR-0003) and overhead line-of-sight
 * (`./regions`, ADR-0017) key off the same structures.
 */
export interface RoofStructure<T> {
  members: T[];
  footprint: Set<string>;
}

/**
 * Group the roof statics into connected structures by 8-adjacency flood-fill of
 * their tile footprints, so two buildings that merely share the crop resolve
 * independently. Non-roof statics are ignored. Pure; does not mutate the input.
 */
export function roofStructures<T extends RoofCandidate>(
  statics: readonly T[],
): RoofStructure<T>[] {
  const roofs = statics.filter((s) => s.roof);
  const structures: RoofStructure<T>[] = [];
  if (roofs.length === 0) return structures;

  // Index roof members by their tile so a flood-fill can walk the footprint.
  const byTile = new Map<string, T[]>();
  for (const s of roofs) {
    const k = key(s.x, s.y);
    (byTile.get(k) ?? byTile.set(k, []).get(k)!).push(s);
  }

  // Flood-fill connected structures (8-adjacency) from the unvisited roof tiles.
  const visited = new Set<string>();
  for (const start of roofs) {
    const startKey = key(start.x, start.y);
    if (visited.has(startKey)) continue;

    const members: T[] = [];
    const footprint = new Set<string>();
    const queue: Tile[] = [{ x: start.x, y: start.y }];
    visited.add(startKey);

    while (queue.length > 0) {
      const { x, y } = queue.pop()!;
      const k = key(x, y);
      footprint.add(k);
      for (const member of byTile.get(k) ?? []) members.push(member);

      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          const nk = key(x + dx, y + dy);
          if (byTile.has(nk) && !visited.has(nk)) {
            visited.add(nk);
            queue.push({ x: x + dx, y: y + dy });
          }
        }
      }
    }

    structures.push({ members, footprint });
  }

  return structures;
}

/**
 * Return the subset of `statics` whose roof should be culled given the local
 * player's tile: the roof of the one structure whose footprint contains the
 * player's tile, or the empty set when the player is outside every roof.
 * Non-roof statics are never returned. Does not mutate the input.
 */
export function hiddenRoofStatics<T extends RoofCandidate>(
  statics: readonly T[],
  playerTile: Tile,
): Set<T> {
  const hidden = new Set<T>();
  for (const structure of roofStructures(statics)) {
    // The player is "under" this structure iff their tile is in its footprint.
    if (structure.footprint.has(key(playerTile.x, playerTile.y))) {
      for (const member of structure.members) hidden.add(member);
    }
  }
  return hidden;
}

/**
 * Whether a drawn entity sits **on a hidden roof** and so must be culled along
 * with it (ADR-0155). The companion to {@link hiddenRoofStatics}: when a
 * structure's roof is hidden for this viewer (they are standing under it), an
 * entity standing on that same structure's *roof* would otherwise render
 * floating over the open interior the viewer can now see into, so it is hidden
 * too. Both decisions cull the **same** `hidden` structure, so the roof tiles and
 * the roof-top entities can never disagree.
 *
 * "On the roof" means the entity's tile is inside the structure's `footprint`
 * **and** it sits at roof/cap level - `elevation` above the GROUND under its
 * tile (the bank roof plate is `z=20`). The ground reference is the optional
 * `landZ` height function (true terrain height, PRD #536 Slice B): a player
 * standing on a hill carries the mirrored terrain z as elevation, which equals
 * `landZ(tile)` and so is ground, never a roof-topper. Absent (or on a flat
 * map, where landZ is 0 everywhere) the test is exactly the old `elevation >
 * 0`. A **ground-level** occupant under the open roof stays visible - seeing
 * into the building is the whole point. `hidden` is `null` (no structure
 * hidden for this viewer: they are outside every roof, or on a roof
 * themselves) culls nothing. Pure: no Pixi, no clock, so it is unit-tested
 * exactly like {@link hiddenRoofStatics}.
 */
export function isEntityOnHiddenRoof<T extends RoofCandidate>(
  hidden: RoofStructure<T> | null | undefined,
  entity: { tile: Tile; elevation: number },
  landZ?: (x: number, y: number) => number,
): boolean {
  if (!hidden) return false;
  const ground = landZ ? landZ(entity.tile.x, entity.tile.y) : 0;
  if (entity.elevation <= ground) return false; // ground-level (incl. hill-top) occupant stays visible
  return hidden.footprint.has(key(entity.tile.x, entity.tile.y));
}
